import React, { useCallback, useState } from "react"

import { SidebarContextMenu } from "../containers/Sidebar/SidebarContextMenu.tsx"

const MIDDLE_BUTTON = 1

const openInNewTab = (route: string) => {
  window.open(route, "_blank", "noopener,noreferrer")
}

// Ctrl on Windows/Linux, Cmd on macOS — the browser's "open link in a new tab" modifier.
const hasNewTabModifier = (event: React.MouseEvent | React.KeyboardEvent) =>
  event.ctrlKey || event.metaKey

/**
 * The main sidebar's "open in a new tab" gestures for any navigating rows:
 * right-click offers Open in new tab, and Ctrl/Cmd+click, Ctrl/Cmd+Enter and a
 * middle click open the row's page in a new browser tab. `resolveRoute` names
 * the page under an event's target, or null where there is none. Spread
 * `handlers` on an element wrapping the rows and render `contextMenu`.
 */
export function useOpenInNewTab(
  resolveRoute: (target: EventTarget) => string | null
) {
  const [menu, setMenu] = useState<{
    x: number
    y: number
    route: string
  } | null>(null)

  const handlers = {
    onContextMenu: (event: React.MouseEvent<HTMLElement>) => {
      const route = resolveRoute(event.target)
      if (!route) return
      event.preventDefault()
      setMenu({ x: event.clientX, y: event.clientY, route })
    },
    // Caught on the way down, so the tab's own click — which would navigate
    // this browser tab — never runs.
    onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
      if (!hasNewTabModifier(event)) return
      const route = resolveRoute(event.target)
      if (!route) return
      event.preventDefault()
      event.stopPropagation()
      openInNewTab(route)
    },
    onKeyDownCapture: (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key !== "Enter" || !hasNewTabModifier(event)) return
      const route = resolveRoute(event.target)
      if (!route) return
      event.preventDefault()
      event.stopPropagation()
      if (!event.repeat) openInNewTab(route)
    },
    onAuxClick: (event: React.MouseEvent<HTMLElement>) => {
      if (event.button !== MIDDLE_BUTTON) return
      const route = resolveRoute(event.target)
      if (!route) return
      event.preventDefault()
      openInNewTab(route)
    },
    // A middle press starts the browser's autoscroll on Windows; over a tab it
    // means "open in a new tab" instead.
    onMouseDown: (event: React.MouseEvent<HTMLElement>) => {
      if (event.button === MIDDLE_BUTTON && resolveRoute(event.target)) {
        event.preventDefault()
      }
    }
  }

  const contextMenu = menu ? (
    <SidebarContextMenu
      open
      x={menu.x}
      y={menu.y}
      onOpenChange={(next) => {
        if (!next) setMenu(null)
      }}
      onOpenInNewTab={() => {
        openInNewTab(menu.route)
        setMenu(null)
      }}
    />
  ) : null

  return { handlers, contextMenu }
}

/** `useOpenInNewTab` for a row of antd Tabs, whose tabs carry `data-node-key`. */
export function useTabsOpenInNewTab(
  routeOf: (key: string) => string | undefined
) {
  const resolveRoute = useCallback(
    (target: EventTarget): string | null => {
      const tab =
        target instanceof Element
          ? target.closest<HTMLElement>("[data-node-key]")
          : null
      if (!tab) return null
      return routeOf(tab.getAttribute("data-node-key") ?? "") ?? null
    },
    [routeOf]
  )
  return useOpenInNewTab(resolveRoute)
}
